import axios from 'axios';
import { authRefreshContract, type AuthRefreshResponse } from '@bliks-io/contracts';
import { env } from '@/config/env';
import { LOCAL_STORAGE_KEYS } from '@/constants';
import { useAuthStore } from '@/stores/auth-store';

/**
 * - `renewed`: a new token is stored.
 * - `dead`: the server refused the session itself — tear down.
 * - `unavailable`: we could not find out (network, 429, 5xx). The session may
 *   be perfectly fine, so the caller must NOT log the user out; failing the
 *   one request and retrying later is the honest answer.
 */
export type RenewalOutcome = 'renewed' | 'dead' | 'unavailable';

/**
 * Matches the shared agent's timeout. Bare axios has none at all, and a
 * renewal that never settles would latch the single-flight promise below —
 * every later 401 would await it forever and the app would silently freeze.
 */
const RENEWAL_TIMEOUT_MS = 30_000;

let refreshInFlight: Promise<RenewalOutcome> | null = null;

/**
 * Renews the access token from the HttpOnly session cookie.
 *
 * Deliberately uses a bare axios call rather than the shared `agent`: the
 * agent's own 401 handler is what calls this, and routing the renewal through
 * it would recurse — a failed renewal would trigger a renewal.
 *
 * Single-flight, for the same reason `teardownSessionToLogin` is: a dashboard
 * fires a dozen queries that all expire at the same instant, and a dozen
 * parallel renewals would race each other at the identity provider. Every
 * caller awaits the one call.
 */
export function refreshSession(): Promise<RenewalOutcome> {
  if (refreshInFlight) {
    return refreshInFlight;
  }

  refreshInFlight = performRefresh().finally(() => {
    refreshInFlight = null;
  });

  return refreshInFlight;
}

async function performRefresh(): Promise<RenewalOutcome> {
  const token = localStorage.getItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);
  // The expired token identifies WHICH session to renew (and in which
  // workspace). Without one there is nothing to renew — a logged-out browser
  // must not reach the endpoint at all.
  if (!token) return 'dead';
  if (useAuthStore.getState().isLoggingOut) return 'dead';

  let data: AuthRefreshResponse;
  try {
    const response = await axios.post<AuthRefreshResponse>(authRefreshContract.path, undefined, {
      baseURL: env.BACKEND_ENDPOINT,
      headers: { Authorization: `Bearer ${token}` },
      // Carries the session cookie; it is the other half of the credential.
      withCredentials: true,
      timeout: RENEWAL_TIMEOUT_MS
    });
    data = response.data;
  } catch (error) {
    // Only the server rejecting the SESSION ends it. A 429 from the shared
    // per-IP limit, a 503 while the IdP restarts, or a dropped connection must
    // not log a user out who is holding a valid session.
    return axios.isAxiosError(error) && isSessionRefused(error.response?.status)
      ? 'dead'
      : 'unavailable';
  }

  // A logout or a workspace switch that landed while this was in flight wins:
  // writing here would resurrect a session the user just ended, or put the old
  // workspace's token back over the new one.
  if (
    useAuthStore.getState().isLoggingOut ||
    localStorage.getItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN) !== token
  ) {
    return 'unavailable';
  }

  useAuthStore.getState().setAuth(data);
  return 'renewed';
}

const isSessionRefused = (status: number | undefined): boolean => status === 401 || status === 403;
